import { useRef } from "react";
import {
  FieldMetadataForTypes as FieldMetadata,
  Issue,
} from "@adsk/acc-issues-types";
import { useIsFieldValuePresentInIssue } from "./useIsFieldValuePresentInIssue";
import usePlacementData from "../IssueDetailPanel/IssueDetailTabPanel/hooks/PlacementFieldHooks/usePlacementData";

/**
 * Separates fields into populated and unpopulated based on their values in an issue.
 *
 * NOTE: Partition is kept stable so fields are not reassigned between populated /
 * unpopulated while the user edits values. It is recomputed when:
 * - the component first mounts,
 * - `sheetAssociation` becomes available after initially being undefined (placement
 *   data loads async),
 * - the issue type changes (new type has different fields / required / visible flags).
 */
export const useGetFilledAndUnpopulatedFields = (
  issue: Issue,
  sortedFields: FieldMetadata[]
): {
  populatedFields: FieldMetadata[];
  unpopulatedFields: FieldMetadata[];
} => {
  const getPlacementData = usePlacementData();
  const { sheetAssociation } = getPlacementData(issue);

  const isFieldValuePresentInIssue = useIsFieldValuePresentInIssue();

  const snapshotRef =
    useRef<{
      populatedFields: FieldMetadata[];
      unpopulatedFields: FieldMetadata[];
    } | null>(null);

  const prevSheetAssociationRef = useRef(sheetAssociation);
  const prevIssueTypeIdRef = useRef(issue.issueTypeId);

  const shouldRecomputeSnapshot =
    snapshotRef.current === null ||
    (prevSheetAssociationRef.current === undefined &&
      sheetAssociation !== undefined) ||
    prevIssueTypeIdRef.current !== issue.issueTypeId;

  if (shouldRecomputeSnapshot) {
    snapshotRef.current = {
      populatedFields: sortedFields.filter(
        field =>
          isFieldValuePresentInIssue(field, issue, sheetAssociation) &&
          field.visible
      ),
      unpopulatedFields: sortedFields.filter(
        field =>
          !isFieldValuePresentInIssue(field, issue, sheetAssociation) &&
          field.visible
      ),
    };
  }

  prevSheetAssociationRef.current = sheetAssociation;
  prevIssueTypeIdRef.current = issue.issueTypeId;

  return snapshotRef.current!;
};
